import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Endpoint, RoleMimetype } from 'constants/API'
import { useAuth0 } from 'utils/auth'
import API, { APIRequestConfig } from './API'

/** Keeps expected failures out of Sentry; the endpoint template stays the fingerprint */
export type APIErrorReportingConfig = Pick<APIRequestConfig, 'ignoreError' | 'ignoreErrorFactory'>

export const _getUrl = <EndpointType extends string>(url: EndpointType, replacements: Record<string, string>) => {
  let finalUrl: string = url

  for (let [key, value] of Object.entries(replacements)) {
    finalUrl = finalUrl.replace(`{${key}}`, encodeURIComponent(value))
  }

  return finalUrl
}

/**
 * Provides wrapped API methods with automatically resolved mimetypes and easy param replacement with automatic URI encoding
 */
export const useAPI = <EndpointType extends string = Endpoint>() => {
  const { roles } = useAuth0()

  function get<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse: true, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<AxiosResponse<ReturnType>>
  function get<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse: false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType>
  function get<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse = false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType | AxiosResponse<ReturnType>> {
    return API.get<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      {
        headers: { Accept: API.decideRoleMimeType(roles) },
        ...options,
      },
      {
        ...errorReporting,
        endpoint,
      }
    ).then(resp => keepResponse ? resp : resp.data)
  }

  function del<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse: true, options?: Omit<AxiosRequestConfig, 'headers'>, errorReporting?: APIErrorReportingConfig): Promise<AxiosResponse<ReturnType>>
  function del<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse: false, options?: Omit<AxiosRequestConfig, 'headers'>, errorReporting?: APIErrorReportingConfig): Promise<ReturnType>
  function del<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, keepResponse = false, options?: Omit<AxiosRequestConfig, 'headers'>, errorReporting?: APIErrorReportingConfig): Promise<ReturnType | AxiosResponse<ReturnType>> {
    return API.delete<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      {
        headers: { Accept: API.decideRoleMimeType(roles) },
        ...options,
      },
      {
        ...errorReporting,
        endpoint,
      }
    ).then(resp => keepResponse ? resp : resp.data)
  }

  function put<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object | undefined, keepResponse: true, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<AxiosResponse<ReturnType>>
  function put<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object | undefined, keepResponse: false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType>
  function put<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object | undefined, keepResponse = false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType | AxiosResponse<ReturnType>> {
    return API.put<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      body,
      {
        headers: { Accept: API.decideRoleMimeType(roles) },
        ...options,
      },
      {
        ...errorReporting,
        endpoint,
      }
    ).then(resp => keepResponse ? resp : resp.data)
  }

  function post<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse: true, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<AxiosResponse<ReturnType>>
  function post<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse: false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType>
  function post<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse = false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType | AxiosResponse<ReturnType>> {
    return API.post<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      body,
      {
        headers: { Accept: API.decideRoleMimeType(roles) },
        ...options,
      },
      {
        ...errorReporting,
        endpoint,
      }
    ).then(resp => keepResponse ? resp : resp.data)
  }

  function patch<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse: true, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<AxiosResponse<ReturnType>>
  function patch<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse: false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType>
  function patch<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, keepResponse = false, options?: AxiosRequestConfig, errorReporting?: APIErrorReportingConfig): Promise<ReturnType | AxiosResponse<ReturnType>> {
    return API.patch<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      body,
      {
        headers: { Accept: API.decideRoleMimeType(roles) },
        ...options,
      },
      {
        ...errorReporting,
        endpoint,
      }
    ).then(resp => keepResponse ? resp : resp.data)
  }

  async function putExternal<ReturnType>(url: string, body: any, options?: AxiosRequestConfig): Promise<AxiosResponse<ReturnType>> {
    return API.put<ReturnType>(
      url,
      body,
      options,
      {
        ignoreBaseUrl: true,
        ignoreAuthorizationHeaders: true,
      }
    )
  }

  // POST to a public (permitAll) endpoint WITHOUT attaching the Authorization header. The backend resource
  // server rejects any present-but-invalid/expired bearer token with 401 before permitAll applies, so
  // attaching the (often stale) SPA token to a public endpoint — e.g. the email-verification link opened
  // from a mail client with no live session — turns it into a 401. Identity here comes from the request
  // token, not the session, so the header is omitted entirely.
  function postPublic<ReturnType>(endpoint: EndpointType, replacements: Record<string, string>, body: object, options?: AxiosRequestConfig): Promise<ReturnType> {
    return API.post<ReturnType>(
      _getUrl<EndpointType>(endpoint, replacements),
      body,
      {
        ...options,
        headers: { Accept: RoleMimetype.DEFAULT, ...options?.headers },
      },
      {
        endpoint,
        ignoreAuthorizationHeaders: true,
      }
    ).then(resp => resp.data)
  }

  return {
    get,
    put,
    putExternal,
    post,
    postPublic,
    patch,
    delete: del,
  }
}

export type UseAPIReturn<T extends string> = ReturnType<typeof useAPI<T>>
